import { Measurement, MeasurementDisplayItem, MeasurementDisplayItemId, MeasurementType } from "@evident/ngx-media-viewer";

export type UnitType = "length" | "area";
export type UnitSystem = "Metric" | "Imperial";
/** The unit as it is stored: on the measurement items of both measurement tabs and in the tenant setting. */
export type UnitLabel = "mm" | "inch";

export const isMetric = (unitSystem: UnitSystem) => unitSystem == "Metric";

/**
 * The unit system of a stored unit ("mm" / "inch"; older tenant configs also hold "MM" / "IN"). Nothing stored means the
 * metric default of the tenant setting; anything that is not millimetres is imperial.
 */
export const unitSystemOfUnitLabel = (label: string | null | undefined): UnitSystem => {
  if (!label) return "Metric";
  return label.toLowerCase() === "mm" ? "Metric" : "Imperial";
};

export const unitLabelOfUnitSystem = (unitSystem: UnitSystem): UnitLabel => (isMetric(unitSystem) ? "mm" : "inch");

export const precisionMetric = "1.2-2";
export const precisionImperial = "1.3-3";

export const precision = (unitSsystem: UnitSystem) => {
  return isMetric(unitSsystem) ? precisionMetric : precisionImperial;
};

export const getUnitLabel = (unit: UnitType, unitSystem: UnitSystem): string => (isMetric(unitSystem) ? getMetricUnit(unit) : getImperialUnit(unit));

const getMetricUnit = (unit: UnitType) => (unit == "area" ? "mm²" : "mm");

const getImperialUnit = (unit: UnitType) => (unit == "area" ? "inch²" : "inch");

/** The measurement types ViSOL offers, in picker order; the media viewer knows more (TipClearance, CircularArc...). */
export const VISOL_MEASUREMENT_TYPES: readonly MeasurementType[] = [
  MeasurementType.DISTANCE,
  MeasurementType.POLYLINE,
  MeasurementType.DEPTH,
  MeasurementType.AREA,
  MeasurementType.POINT_TO_LINE,
  MeasurementType.TIP_DEFECT
];

/** One value ViSOL lists for a measurement. */
export interface MeasurementResultItem {
  /** Translation key of the value label (the measurement type for a single value). */
  label: string;
  labelParams?: Record<string, number>;
  value: number;
  unitType: UnitType;
  /** The placed point the value belongs to, for the measurements that have one value per point. */
  pointId?: string;
}

/** Labels of the display items that are not the single primary value of their measurement. */
const DISPLAY_ITEM_LABELS: Partial<Record<MeasurementDisplayItemId, string>> = {
  area: "measurement.Area",
  edge1Distance: "measurement.edge1Distance",
  edge2Distance: "measurement.edge2Distance"
};

export const unitTypeOf = (result: MeasurementDisplayItem | undefined): UnitType => (result?.quantity === "area" ? "area" : "length");

/**
 * Every value ViSOL lists for a measurement: one per placed point for Depth and PointToLine (the viewer only
 * reports the last one as the primary display item), otherwise every display item (TipDefect: the area and
 * the two edge distances).
 */
export const getMeasurementResults = (measurement: Measurement): MeasurementResultItem[] => {
  if (measurement.hasSubPoints()) {
    return measurement.points.map((point, index) => ({
      label: "measurement.point",
      labelParams: { index: index + 1 },
      value: point.length,
      unitType: "length",
      pointId: point.id
    }));
  }

  return measurement.getDisplayItems().map((item) => ({
    label: item.id === "primary" ? `measurement.${measurement.type}` : (DISPLAY_ITEM_LABELS[item.id] ?? `measurement.${item.id}`),
    value: item.value,
    unitType: unitTypeOf(item)
  }));
};
